import { useFormatters, useI18n } from '../../i18n';
import type { VosJsonItem } from '../../lib/vosjson';
import { itemColorOf, type Neighbour } from './mapData';

/**
 * Everything the map knows about one item.
 *
 * Read from the VOSviewer JSON rather than from the renderer's own item,
 * because the renderer keeps one weight per item and the file keeps all of
 * them — and "Total link strength" next to "Occurrences" is exactly what a
 * reader is looking at the panel for.
 */
export function ItemDetailPanel({
  item,
  neighbours,
  onSelect,
}: {
  item: VosJsonItem | null;
  neighbours: readonly Neighbour[];
  onSelect: (id: string) => void;
}) {
  const { t } = useI18n();
  const fmt = useFormatters();

  if (!item) {
    return <p className="aside__empty">{t('result.detail.empty')}</p>;
  }

  const weights = item.weights ?? (typeof item.weight === 'number' ? { '': item.weight } : {});
  const scores = item.scores ?? (typeof item.score === 'number' ? { '': item.score } : {});

  return (
    <div>
      <div className="row" style={{ gap: 'var(--sp-2)', alignItems: 'flex-start' }}>
        <span
          className="legend__swatch"
          style={{ background: itemColorOf(item), marginTop: '0.35rem' }}
          aria-hidden
        />
        <div style={{ minWidth: 0 }}>
          <p className="detail__label">{item.label ?? String(item.id ?? '')}</p>
          {item.sublabel ? <p className="detail__sublabel">{item.sublabel}</p> : null}
          {item.description ? <p className="detail__sublabel">{item.description}</p> : null}
        </div>
      </div>

      <div className="kv">
        {typeof item.cluster === 'number' && item.cluster > 0 ? (
          <>
            <span className="kv__key">{t('result.detail.cluster')}</span>
            <span className="kv__value">{item.cluster}</span>
          </>
        ) : null}
        <span className="kv__key">{t('result.detail.coordinates')}</span>
        <span className="kv__value">
          {fmt.num(item.x ?? 0)}, {fmt.num(item.y ?? 0)}
        </span>
      </div>

      {Object.keys(weights).length > 0 ? (
        <>
          <p className="aside__title" style={{ marginTop: 'var(--sp-4)' }}>
            {t('result.detail.weights')}
          </p>
          <div className="kv" style={{ marginTop: 0 }}>
            {Object.entries(weights).map(([key, value]) => (
              <Row key={`w-${key}`} label={key} value={value} format={fmt.num} />
            ))}
          </div>
        </>
      ) : null}

      {Object.keys(scores).length > 0 ? (
        <>
          <p className="aside__title" style={{ marginTop: 'var(--sp-4)' }}>
            {t('result.detail.scores')}
          </p>
          <div className="kv" style={{ marginTop: 0 }}>
            {Object.entries(scores).map(([key, value]) => (
              <Row key={`s-${key}`} label={key} value={value} format={fmt.num} />
            ))}
          </div>
        </>
      ) : null}

      <p className="aside__title" style={{ marginTop: 'var(--sp-4)' }}>
        {t('result.detail.neighbours')}
      </p>
      {neighbours.length === 0 ? (
        <p className="aside__empty">{t('result.detail.noNeighbours')}</p>
      ) : (
        <div className="neighbours">
          {neighbours.map((neighbour) => (
            <button
              key={neighbour.id}
              type="button"
              className="neighbour"
              onClick={() => onSelect(neighbour.id)}
            >
              <span>{neighbour.label}</span>
              <span className="neighbour__strength">{fmt.num(neighbour.strength)}</span>
            </button>
          ))}
        </div>
      )}

      {item.url ? (
        <p style={{ marginTop: 'var(--sp-4)' }}>
          <a href={item.url} target="_blank" rel="noreferrer noopener">
            {t('result.detail.openUrl')}
          </a>
        </p>
      ) : null}
    </div>
  );
}

function Row({
  label,
  value,
  format,
}: {
  label: string;
  value: number | null | undefined;
  format: (n: number) => string;
}) {
  return (
    <>
      <span className="kv__key">{label || '—'}</span>
      <span className="kv__value">
        {typeof value === 'number' && Number.isFinite(value) ? format(value) : '—'}
      </span>
    </>
  );
}
